/**
 * UUID validation utilities for user identification
 * @module validation
 */

/**
 * Regular expression for UUID v4 format validation
 * @constant {RegExp}
 * @private
 */
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Validates if a string is a valid UUID v4
 * @param {string} uuid - The string to validate
 * @returns {boolean} True if the string is a valid UUID v4, false otherwise
 * @example
 * const isValid = isValidUUID('550e8400-e29b-41d4-a716-446655440000');
 * console.log(isValid); // true
 */
export const isValidUUID = (uuid) => {
    if (!uuid || typeof uuid !== 'string') return false;
    return UUID_V4_REGEX.test(uuid);
};

/**
 * Ensures a value is a valid UUID v4, throws an error if not
 * @param {string} uuid - The UUID to validate
 * @param {string} [paramName='UUID'] - Name of the parameter for error message
 * @throws {Error} If the UUID is invalid
 * @example
 * try {
 *   validateUUID('550e8400-e29b-41d4-a716-446655440000', 'userId');
 * } catch (error) {
 *   console.error(error.message);
 * }
 */
export const validateUUID = (uuid, paramName = 'UUID') => {
    if (!isValidUUID(uuid)) {
        throw new Error(`Invalid ${paramName} format`);
    }
};

/**
 * Checks if a value might be a legacy numeric ID
 * @param {string|number} id - The ID to check
 * @returns {boolean} True if the ID appears to be a legacy numeric ID
 * @example
 * const isLegacy = isLegacyId('123');
 * console.log(isLegacy); // true
 */
export const isLegacyId = (id) => {
    if (typeof id === 'number') return true;
    return /^\d+$/.test(id);
};

/**
 * Converts any ID to string format
 * @param {string|number} id - The ID to convert
 * @returns {string} The ID in string format
 * @example
 * const stringId = formatId(123);
 * console.log(stringId); // '123'
 */
export const formatId = (id) => {
    return id?.toString() || '';
};

/**
 * Validates an ID, checking for both UUID and legacy format
 * @param {string|number} id - The ID to validate
 * @param {Object} [options] - Validation options
 * @param {boolean} [options.allowLegacy=true] - Whether to allow legacy numeric IDs
 * @param {string} [options.paramName='ID'] - Name of the parameter for error message
 * @returns {boolean} True if the ID is valid
 * @throws {Error} If the ID is invalid
 * @example
 * try {
 *   validateId('550e8400-e29b-41d4-a716-446655440000', { allowLegacy: false });
 * } catch (error) {
 *   console.error(error.message);
 * }
 */
export const validateId = (id, { allowLegacy = true, paramName = 'ID' } = {}) => {
    if (!id) {
        throw new Error(`${paramName} is required`);
    }

    if (isValidUUID(id)) {
        return true;
    }

    if (allowLegacy && isLegacyId(id)) {
        return true;
    }

    throw new Error(`Invalid ${paramName} format`);
}; 